/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useRef, forwardRef, ForwardedRef, HTMLAttributes } from 'react';
import { Control, Controller, Path, FieldError } from 'react-hook-form';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import {
    FormControl,
    FormControlProps,
    Autocomplete,
    AutocompleteProps,
    Checkbox,
    TextField,
    TextFieldProps,
    CircularProgress,
    Grow,
    Paper,
} from '@mui/material';
import { usePhrase } from 'hooks/general/usePhrase/usePhrase';
import { LabelWithTooltip } from '@/components/forms/other/LabelWithTooltip/LabelWithTooltip';
interface IAutoCompleteData {
    label: string;
    value: string | null;
    useRawLabel?: boolean;
}
interface CustomAutoCompleteFieldProps<
    F extends FieldValues,
    T,
    M extends boolean | undefined,
    D extends boolean | undefined,
> {
    control: Control<F>;
    name: Path<F>;
    label: string | number;
    data: T[];
    multiple?: boolean;
    loading?: boolean;
    required?: boolean;
    showCheckbox?: boolean;
    error?: FieldError | null;
    autocompleteProps?: Omit<
        AutocompleteProps<T, M, D, any>,
        'name' | 'options' | 'loading' | 'renderInput'
    >;
    textFieldProps?: Omit<TextFieldProps, 'name' | 'required' | 'label'>;
}

const PaperComponent = (
    paperProps: HTMLAttributes<HTMLElement>,
    ref: null | ForwardedRef<HTMLDivElement>,
) => {
    return (
        <Grow in>
            <Paper {...paperProps} ref={ref} />
        </Grow>
    );
};

const PaperComponentForward = forwardRef(PaperComponent);

type AutoCompleteFieldProps<TFieldValues extends FieldValues> = CustomAutoCompleteFieldProps<
    TFieldValues,
    IAutoCompleteData | string | any,
    boolean | undefined,
    boolean | undefined
> &
    Omit<FormControlProps, 'error' | 'label' | 'name'>;

export const AutoCompleteField = <TFieldValues extends FieldValues>(
    props: AutoCompleteFieldProps<TFieldValues>,
) => {
    const { t } = usePhrase();
    const {
        control,
        name,
        label,
        data,
        multiple,
        loading,
        required,
        showCheckbox,
        error,
        autocompleteProps,
        textFieldProps,
        ...rest
    } = props;

    const formControlRef = useRef<HTMLDivElement>(null);
    const labelRef = useRef<HTMLLabelElement>(null);

    // disable label animation on page load
    formControlRef.current?.classList.add('label-noanim');

    return (
        <FormControl {...rest} ref={formControlRef}>
            <Controller
                name={name}
                control={control}
                render={({
                    field: { onChange, onBlur, value, ref, ...fieldRest },
                    fieldState: { error: fieldError },
                }) => {
                    const isError = Boolean(error || fieldError);
                    const currentValue = multiple
                        ? (value || []).map((i: any) => data.find((j) => (j.value || j) === i))
                        : data.find((i) => (i.value || i) === value) || null;

                    return (
                        <Autocomplete
                            {...autocompleteProps}
                            value={currentValue}
                            loading={loading}
                            multiple={multiple}
                            options={data}
                            disableClearable={autocompleteProps?.disableClearable ?? true}
                            disableCloseOnSelect={
                                typeof autocompleteProps?.disableCloseOnSelect === 'boolean'
                                    ? autocompleteProps.disableCloseOnSelect
                                    : !!multiple
                            }
                            isOptionEqualToValue={
                                autocompleteProps?.isOptionEqualToValue
                                    ? autocompleteProps.isOptionEqualToValue
                                    : (option, value) => {
                                          return value
                                              ? option.value === (value?.value || value)
                                              : false;
                                      }
                            }
                            getOptionLabel={
                                autocompleteProps?.getOptionLabel
                                    ? autocompleteProps.getOptionLabel
                                    : (option) => {
                                          return `${
                                              typeof option?.label === 'string'
                                                  ? option.label
                                                  : t(option.label as number)
                                          }`;
                                      }
                            }
                            onChange={(event, value, reason, details) => {
                                const changedVal = Array.isArray(value)
                                    ? value.map((i: any) => i?.value || i)
                                    : value?.value || value;
                                onChange(changedVal);
                                if (autocompleteProps?.onChange) {
                                    autocompleteProps.onChange(event, value, reason, details);
                                }
                            }}
                            renderOption={
                                autocompleteProps?.renderOption ??
                                (showCheckbox
                                    ? (props, option, { selected }) => (
                                          <li {...props}>
                                              <Checkbox
                                                  sx={{ marginRight: 1 }}
                                                  checked={selected}
                                              />
                                              {autocompleteProps?.getOptionLabel?.(option) ||
                                                  option?.label ||
                                                  option}
                                          </li>
                                      )
                                    : undefined)
                            }
                            onBlur={(event) => {
                                onBlur();
                                if (typeof autocompleteProps?.onBlur === 'function') {
                                    autocompleteProps.onBlur(event);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    name={name}
                                    required={required}
                                    label={<LabelWithTooltip label={label} labelRef={labelRef} />}
                                    {...textFieldProps}
                                    {...params}
                                    error={isError}
                                    helperText={
                                        fieldError?.message
                                            ? fieldError?.message
                                            : error?.message
                                              ? error?.message
                                              : ''
                                    }
                                    inputRef={ref}
                                    onFocus={() => {
                                        const element = formControlRef.current;
                                        if (element?.classList.contains('label-noanim')) {
                                            element?.classList.remove('label-noanim');
                                        }
                                    }}
                                    slotProps={{
                                        ...textFieldProps?.slotProps,
                                        input: {
                                            ...params.InputProps,
                                            ...textFieldProps?.slotProps?.input,
                                            endAdornment: (
                                                <>
                                                    {loading ? (
                                                        <CircularProgress
                                                            color="inherit"
                                                            size={20}
                                                        />
                                                    ) : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        },
                                        inputLabel: {
                                            ...textFieldProps?.slotProps?.inputLabel,
                                            ref: labelRef,
                                        },
                                    }}
                                />
                            )}
                            PaperComponent={PaperComponentForward}
                            {...fieldRest}
                        />
                    );
                }}
            />
        </FormControl>
    );
};
